// One key slot of an action: click (or Enter) and press a key, mouse button
// or wheel to bind it; Escape cancels, Backspace / Delete clears the slot.

import { useEffect, useRef, useState } from 'preact/hooks';
import { keyLabel } from '../../game/binds';

export interface KeyBindProps {
  code: string | null;
  /** Resolves with the next pressed input (Input.captureNext). */
  capture: () => Promise<string>;
  /** Called when listening is abandoned (unmount): stops the pending capture. */
  cancel?: () => void;
  onChange: (code: string | null) => void;
  /** The key is also bound to another action. */
  conflict?: boolean;
  label: string;
}

export function KeyBind({ code, capture, cancel, onChange, conflict, label }: KeyBindProps) {
  const [listening, setListening] = useState(false);
  const alive = useRef(true);
  const pending = useRef(false);
  useEffect(
    () => () => {
      alive.current = false;
      if (pending.current) cancel?.();
    },
    [],
  );

  const start = async () => {
    if (listening) return;
    setListening(true);
    pending.current = true;
    const next = await capture();
    pending.current = false;
    if (!alive.current) return;
    setListening(false);
    if (next === 'Escape') return;
    onChange(next === 'Backspace' || next === 'Delete' ? null : next);
  };

  return (
    <button
      type="button"
      class={`keybind${listening ? ' listening' : ''}${code ? '' : ' empty'}${conflict ? ' conflict' : ''}`}
      aria-label={`${label}: ${listening ? 'press a key' : keyLabel(code)}`}
      title={conflict ? 'Also bound to another action' : undefined}
      onClick={start}
    >
      {listening ? 'Press a key…' : keyLabel(code)}
      {conflict && !listening && <span class="keybind-warn" aria-hidden="true">!</span>}
    </button>
  );
}
